// external
import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import GroupsIcon from '@mui/icons-material/Groups';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Ward } from '../../models/training.models';
import { sortByLastName } from '../../helpers/general.helpers';
import { UserAvatar } from '../user-avatar/user-avatar.component';

export interface COEWardTeamCardProps {
  teamId: string;
  name: string;
  color: string;
  status: string;
  description: string;
  wards: Ward[];
  plan: string;
  athleteCount?: number;
  coachCount?: number;
  sessionCount?: number;
}

const ORANGE = '#F97316';

const COEWardTeamCardComponent: FC<COEWardTeamCardProps> = (props) => {
  const navigate = useNavigate();
  const planLabel = props.plan === 'free' ? 'BASIC' : 'PRO';
  const sortedWards = sortByLastName(props.wards ?? []);

  return (
    <Card
      onClick={() => navigate(`/teams/${props.teamId}/dashboard`)}
      sx={{
        width: '100%',
        position: 'relative',
        cursor: 'pointer',
        borderLeft: `4px solid ${ORANGE}`,
        borderRadius: '8px',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.08)',
        '&:hover': {
          boxShadow: '0px 3px 10px rgba(0,0,0,0.12)',
        },
        backgroundColor: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      {props.status !== 'active' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.25)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      <CardContent sx={{ p: 2, '&:last-child': { pb: 0 } }}>
        {/* Top row: icon + name/desc + chevron */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              backgroundColor: '#FFF7ED',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <GroupsIcon sx={{ color: ORANGE, fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
              {props.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
              {props.description}
            </Typography>

            {/* Plan badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
              <Box
                sx={{
                  px: 0.75,
                  py: 0.15,
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  display: 'inline-flex',
                }}
              >
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem', color: '#6B7280' }}>
                  {planLabel}
                </Typography>
              </Box>
            </Box>
          </Box>

          <ChevronRightIcon sx={{ color: '#9CA3AF', mt: 0.5, flexShrink: 0 }} />
        </Box>

        {/* Ward athlete names */}
        {sortedWards.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: '6px',
              backgroundColor: '#FFF7ED',
            }}
          >
            {sortedWards.map((ward) => (
              <Box key={ward.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UserAvatar name={ward.name} size={24} fontSize="0.6rem" />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Athlete
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: '0.8rem', color: ORANGE, lineHeight: 1.2 }}
                  >
                    {ward.name}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Stats row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around', pb: 1 }}>
          {[
            { value: props.athleteCount ?? 0, label: 'ATHLETES' },
            { value: props.coachCount ?? 0, label: 'COACHES' },
            { value: props.sessionCount ?? 0, label: 'SESSIONS' },
          ].map((stat) => (
            <Box key={stat.label} sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', letterSpacing: '0.04em' }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default COEWardTeamCardComponent;
